import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ParameterTable } from './ParameterTable';
import { SchemaViewer } from './SchemaViewer';
import { CodeExample } from './CodeExample';
import { resolveResponse } from '@/lib/openapiResponses';
import type { EndpointInfo, OpenAPISpec, Response, MediaType, Schema } from '@/types/openapi';

interface TryResult {
  status: number;
  statusText: string;
  time: number;
  body: string;
}

interface EndpointCardProps {
  endpoint: EndpointInfo;
  spec: OpenAPISpec;
}

const methodColors: Record<string, string> = {
  get: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  post: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  put: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
  delete: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  patch: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
};

export function EndpointCard({ endpoint, spec }: EndpointCardProps) {
  const [activeTab, setActiveTab] = useState('request');
  const [tryResult, setTryResult] = useState<TryResult | null>(null);
  const [tryLoading, setTryLoading] = useState(false);
  const apiKey = localStorage.getItem('apiKey') || 'your-api-key-here';
  const baseUrl = window.location.origin;

  const { path, method, operation } = endpoint;
  // Operations may override the /api/v1 server (e.g. /health is served at the root).
  const serverPrefix = (operation.servers?.[0]?.url ?? '/api/v1').replace(/\/$/, '');

  // Build final path with parameters
  const buildFinalPath = () => {
    let finalPath = path;
    if (operation.parameters) {
      for (const param of operation.parameters) {
        if (param.in === 'path') {
          finalPath = finalPath.replace(`{${param.name}}`, String(param.schema?.example || '1'));
        }
      }
    }
    const queryParams: string[] = [];
    if (operation.parameters) {
      for (const param of operation.parameters) {
        if (param.in === 'query' && param.schema?.example) {
          queryParams.push(`${param.name}=${param.schema.example}`);
        }
      }
    }
    if (queryParams.length > 0) {
      finalPath += `?${queryParams.join('&')}`;
    }
    return finalPath;
  };

  // Execute request
  const executeRequest = async () => {
    setTryLoading(true);
    setTryResult(null);
    const startTime = Date.now();

    try {
      const finalPath = buildFinalPath();
      const url = `${baseUrl}${serverPrefix}${finalPath}`;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey,
      };

      let body: string | undefined;
      if (['post', 'put', 'patch'].includes(method)) {
        const content = operation.requestBody?.content?.['application/json'];
        if (content?.example) {
          body = JSON.stringify(content.example);
        } else if (content?.schema) {
          const sample = generateSampleBody(content.schema as Schema, spec);
          if (sample) {
            body = JSON.stringify(sample);
          }
        }
      }

      const response = await fetch(url, {
        method: method.toUpperCase(),
        headers,
        body,
      });

      const responseBody = await response.text();
      setTryResult({
        status: response.status,
        statusText: response.statusText,
        time: Date.now() - startTime,
        body: responseBody,
      });
    } catch (error) {
      setTryResult({
        status: 0,
        statusText: 'Network Error',
        time: Date.now() - startTime,
        body: error instanceof Error ? error.message : 'Unknown error',
      });
    }

    setTryLoading(false);
  };

  // Generate curl command
  const generateCurl = () => {
    const headers = [
      '-H "Content-Type: application/json"',
      `-H "X-API-Key: ${apiKey}"`,
    ];

    const finalPath = buildFinalPath();

    let body = '';
    if (operation.requestBody) {
      const content = operation.requestBody.content?.['application/json'];
      if (content?.example) {
        body = `-d '${JSON.stringify(content.example)}'`;
      } else if (content?.schema) {
        const sampleBody = generateSampleBody(content.schema as Schema, spec);
        if (sampleBody) {
          body = `-d '${JSON.stringify(sampleBody)}'`;
        }
      }
    }

    const curlMethod = method.toUpperCase();
    const fullUrl = `${baseUrl}${serverPrefix}${finalPath}`;

    return `curl -X ${curlMethod} "${fullUrl}" \\
  ${headers.join('\\\n  ')}${body ? `\\${body}` : ''}`;
  };

  // The first 2xx response is shown in full; the rest are listed below it.
  const successCode = ['200', '201', '202'].find((code) => operation.responses?.[code]);

  const getResponseSchema = (response: Response | undefined): MediaType | undefined => {
    return response?.content?.['application/json'];
  };

  const getExampleResponse = (response: Response | undefined): string => {
    const mediaType = getResponseSchema(response);
    if (mediaType?.example !== undefined) {
      return JSON.stringify(mediaType.example, null, 2);
    }
    if (mediaType?.schema) {
      const sample = generateSampleBody(mediaType.schema, spec);
      if (sample) {
        return JSON.stringify(sample, null, 2);
      }
    }
    return '{ "data": {} }';
  };

  const successRef = successCode ? operation.responses?.[successCode] : undefined;
  const successResponse = successRef ? resolveResponse(successRef, spec) : undefined;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-3">
          <Badge className={methodColors[method] || 'bg-gray-100 text-gray-700'}>
            {method.toUpperCase()}
          </Badge>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <code className="text-lg font-mono">{path}</code>
            </div>
            {operation.summary && (
              <CardTitle className="text-base mt-1">{operation.summary}</CardTitle>
            )}
          </div>
        </div>
        {operation.description && (
          <p className="text-sm text-muted-foreground mt-2">{operation.description}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="request">Request</TabsTrigger>
            <TabsTrigger value="response">Response</TabsTrigger>
            <TabsTrigger value="curl">cURL</TabsTrigger>
            <TabsTrigger value="try">Try</TabsTrigger>
          </TabsList>

          <TabsContent value="request" className="space-y-4 mt-4">
            {/* Parameters */}
            {operation.parameters && operation.parameters.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Parameters</h4>
                <ParameterTable parameters={operation.parameters} />
              </div>
            )}

            {/* Request Body */}
            {operation.requestBody && (
              <div>
                <h4 className="text-sm font-semibold mb-2">
                  Request Body
                  {operation.requestBody.required && (
                    <Badge variant="destructive" className="ml-2 text-xs">Required</Badge>
                  )}
                </h4>
                <div className="rounded-lg border bg-muted/30 p-4">
                  <SchemaViewer
                    schema={operation.requestBody.content?.['application/json']?.schema}
                    spec={spec}
                  />
                </div>
                {operation.requestBody.content?.['application/json']?.example !== undefined && (
                  <div className="mt-2">
                    <h5 className="text-xs text-muted-foreground mb-1">Example:</h5>
                    <CodeExample
                      code={JSON.stringify(
                        operation.requestBody.content?.['application/json']?.example as object,
                        null,
                        2
                      )}
                      language="json"
                    />
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="response" className="space-y-4 mt-4">
            {/* Success Response */}
            {successResponse && (
              <div>
                <h4 className="text-sm font-semibold mb-2">
                  <Badge variant="secondary" className="mr-2">{successCode}</Badge>
                  {successResponse.description}
                </h4>
                <div className="rounded-lg border bg-muted/30 p-4">
                  <SchemaViewer
                    schema={getResponseSchema(successResponse)?.schema}
                    spec={spec}
                  />
                </div>
                <div className="mt-2">
                  <h5 className="text-xs text-muted-foreground mb-1">Example Response:</h5>
                  <CodeExample code={getExampleResponse(successResponse)} language="json" />
                </div>
              </div>
            )}

            {/* Other Responses */}
            {operation.responses && (
              <div className="space-y-2">
                {Object.entries(operation.responses)
                  .filter(([code]) => code !== successCode)
                  .map(([code, ref]) => {
                    const response = resolveResponse(ref, spec);
                    const example = getResponseSchema(response)?.example;
                    return (
                      <div key={code} className="rounded-lg border bg-muted/30 p-3">
                        <div className="flex items-start gap-2">
                          <Badge
                            variant={parseInt(code) >= 400 ? 'destructive' : 'secondary'}
                          >
                            {code}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {response?.description}
                          </span>
                        </div>
                        {example !== undefined && (
                          <CodeExample
                            className="mt-2"
                            code={JSON.stringify(example, null, 2)}
                            language="json"
                          />
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="curl" className="mt-4">
            <CodeExample code={generateCurl()} language="bash" />
          </TabsContent>

          <TabsContent value="try" className="space-y-4 mt-4">
            {/* Request info */}
            <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Badge className={methodColors[method] || 'bg-gray-100 text-gray-700'}>
                  {method.toUpperCase()}
                </Badge>
                <code className="font-mono">{buildFinalPath()}</code>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Authorization:</span>
                <Badge variant="outline">
                  X-API-Key: {apiKey ? '✓ Set' : '✗ Not set'}
                </Badge>
              </div>
              <Button onClick={executeRequest} disabled={tryLoading}>
                {tryLoading ? 'Executing...' : 'Execute Request'}
              </Button>
            </div>

            {/* Response */}
            {tryResult && (
              <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant={tryResult.status >= 200 && tryResult.status < 300 ? 'default' : 'destructive'}>
                      {tryResult.status} {tryResult.statusText}
                    </Badge>
                  </div>
                  <span className="text-sm text-muted-foreground">{tryResult.time}ms</span>
                </div>
                <CodeExample code={tryResult.body} language="json" />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

// Helper function to generate sample body from schema
function generateSampleBody(schema: unknown, spec: OpenAPISpec): Record<string, unknown> | null {
  if (!schema || typeof schema !== 'object') return null;

  const s = schema as Record<string, unknown>;

  // Handle $ref
  if (s.$ref) {
    const refPath = (s.$ref as string).split('/').slice(1);
    let resolved: unknown = spec;
    for (const part of refPath) {
      if (typeof resolved === 'object' && resolved !== null && part in resolved) {
        resolved = (resolved as Record<string, unknown>)[part];
      } else {
        return null;
      }
    }
    return generateSampleBody(resolved, spec);
  }

  if (s.type !== 'object' || !s.properties) return null;

  const properties = s.properties as Record<string, unknown>;
  const required = (s.required as string[]) || [];
  const result: Record<string, unknown> = {};

  for (const [propName, propSchema] of Object.entries(properties)) {
    // Only include required fields to keep sample minimal
    if (required.includes(propName) || propName === 'name') {
      const ps = propSchema as Record<string, unknown>;
      if (ps.example !== undefined) {
        result[propName] = ps.example;
      } else if (ps.type === 'string') {
        const enumValues = ps.enum as string[] | undefined;
        result[propName] = enumValues && enumValues.length > 0 ? enumValues[0] : 'string';
      } else if (ps.type === 'number' || ps.type === 'integer') {
        result[propName] = 1;
      } else if (ps.type === 'boolean') {
        result[propName] = true;
      } else if (ps.type === 'object') {
        result[propName] = generateSampleBody(propSchema, spec) || {};
      } else if (ps.type === 'array') {
        result[propName] = [];
      }
    }
  }

  return result;
}